<template>
  <Modal
    :modal-style="{ maxWidth: '100%' }"
    persistent
    title="Validate Approvals"
  >
    <QTable
      :columns="columns"
      :loading="loading"
      row-key="name"
      :rows="requestRows"
      table-class="q-mb-md"
      wrap-cells
    >
      <template #body="props">
        <QTr
          :class="kebabCase(props.row.action)"
          :props="props"
        >
          <QTd
            key="expandNotes"
            class="expand-notes"
            :props="props"
          >
            <QBtn
              v-if="props.row.notes"
              color="accent"
              dense
              flat
              :icon="props.expand ? 'mode_comment' : 'comment_bank'"
              @click="props.expand = !props.expand"
            />
          </QTd>
          <QTd
            key="requestType"
            class="wrap-table-content"
            :props="props"
          >
            {{ actionForRow(props.row) }}
          </QTd>
          <QTd
            key="firstName"
            class="wrap-table-content"
            :props="props"
          >
            {{ props.row.firstName }}
            <QPopupEdit
              v-if="newUserRow(props.row)"
              v-slot="scope"
              v-model="props.row.firstName"
              buttons
              title="Update first name"
            >
              <InputText
                v-model="scope.value"
                dense
                type="text"
                :validations="['notBlank']"
              />
            </QPopupEdit>
          </QTd>
          <QTd
            key="lastName"
            class="wrap-table-content"
            :props="props"
          >
            {{ props.row.lastName }}
            <QPopupEdit
              v-if="newUserRow(props.row)"
              v-slot="scope"
              v-model="props.row.lastName"
              buttons
              title="Update last name"
            >
              <InputText
                v-model="scope.value"
                dense
                type="text"
                :validations="['notBlank']"
              />
            </QPopupEdit>
          </QTd>
          <QTd
            key="phoneNumber"
            class="wrap-table-content"
            :props="props"
          >
            {{ props.row.phoneNumber }}
            <div
              v-if="
                newUserRow(props.row) &&
                !isPhoneNumberValid(props.row.phoneNumber)
              "
              class="invalid-phone-number"
            >
              <QIcon name="warning" />
              Invalid Format
              <StreemTooltip>
                Please ensure the phone number is in the format 0123456789 or
                +6112345678 - without spaces
              </StreemTooltip>
            </div>
            <QPopupEdit
              v-if="newUserRow(props.row)"
              v-slot="scope"
              v-model="props.row.phoneNumber"
              buttons
              title="Update phone number"
            >
              <InputText
                v-model="scope.value"
                dense
                optional
                type="text"
              />
            </QPopupEdit>
          </QTd>
          <QTd
            key="email"
            class="wrap-table-content"
            :props="props"
          >
            {{ props.row.email }}
            <QPopupEdit
              v-if="newUserRow(props.row)"
              v-slot="scope"
              v-model="props.row.email"
              buttons
              title="Update email"
            >
              <InputText
                v-model="scope.value"
                dense
                type="text"
              />
            </QPopupEdit>
          </QTd>
          <template v-if="newUserRow(props.row)">
            <QTd
              key="briefSetId"
              class="nowrap-table-content"
              :props="props"
            >
              <BriefSetSelector
                v-model="props.row.briefSetId"
                dense
                label="Select Brief Set"
                :organisation-id="props.row.requester.organisation.id"
              />
            </QTd>
            <QTd
              key="roleId"
              class="nowrap-table-content"
              :props="props"
            >
              <RoleDropdown
                v-model="props.row.roleId"
                hide-view-button
              />
            </QTd>
            <QTd
              key="organisationTeamId"
              class="wrap-table-content"
              :props="props"
            >
              <TeamDropdown
                v-if="props.row.organisationTeamIds.length"
                v-model="props.row.organisationTeamIds"
                multiple
                option-label="name"
                :options="
                  organisations[props.row.requester.organisation.id]
                    .organisationTeams
                "
              />
              <TeamDropdown
                v-else
                v-model="props.row.organisationTeamId"
                option-label="name"
                :options="
                  organisations[props.row.requester.organisation.id]
                    .organisationTeams
                "
              />
              <span
                v-if="props.row.teamNotFound"
                class="small text-error"
              >
                Requested Team: <strong>{{ props.row.team }}</strong>
              </span>
            </QTd>
            <QTd
              key="welcomeUser"
              class="wrap-table-content justify-center"
              :props="props"
            >
              <Checkbox v-model="props.row.sendWelcomeEmail" />
            </QTd>
          </template>
          <template v-else>
            <QTd
              key="briefSetId"
              class="nowrap-table-content"
              :colspan="props.row.action === 'activate' ? 3 : 4"
              :props="props"
            >
              <template v-if="props.row.user.deletionWarnings.length">
                Deactivation Warnings:
                {{ toSentence(props.row.user.deletionWarnings) }}
              </template>
              <template v-else-if="props.row.action === 'activate'">
                Safe to Activate
              </template>
              <template v-else> Safe to Deactivate </template>
            </QTd>
            <QTd
              v-if="props.row.action === 'activate'"
              key="welcomeUser"
              class="wrap-table-content justify-center"
              :props="props"
            >
              <Checkbox v-model="props.row.sendWelcomeEmail" />
            </QTd>
          </template>
        </QTr>
        <QTr
          v-show="props.expand"
          :key="props.row.object.id"
          :props="props"
        >
          <QTd colspan="10">
            <div class="text-left">Notes: {{ props.row.notes }}</div>
          </QTd>
        </QTr>
      </template>
    </QTable>
    <template #buttons="{ close }">
      <BaseButton
        class="q-ma-sm"
        flat
        label="Cancel"
        @click="close"
      />
      <BaseButton
        class="q-ma-sm"
        color="primary"
        :disabled="invalidForm"
        label="Approve"
        @click="submit"
      />
    </template>
  </Modal>
</template>

<script>
import { groupBy, kebabCase } from "lodash-es";
import { storeToRefs } from "pinia";

import { BaseButton, InputText } from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import { toSentence } from "shared/helpers/array";
import { pluralize } from "shared/helpers/string";
import { Organisation, UserRequest } from "shared/resources";

import BriefSetSelector from "src/components/briefSets/BriefSetSelector.vue";
import RoleDropdown from "src/components/common/RoleDropdown.vue";
import TeamDropdown from "src/components/common/TeamDropdown.vue";
import { useGlobalAdminStore } from "src/stores/global";

const ACTIONS = {
  deactivate: "Deactivate",
  new_user: "New User",
};

export default {
  name: "ApproveUserRequestModal",
  components: {
    BaseButton,
    BriefSetSelector,
    InputText,
    RoleDropdown,
    TeamDropdown,
    Modal,
  },
  props: {
    userRequests: {
      type: [Object, Array],
      required: true,
    },
  },
  emits: ["approve"],
  setup() {
    const globalAdminStore = useGlobalAdminStore();
    const { roles } = storeToRefs(globalAdminStore);

    return { roles };
  },
  data() {
    return {
      organisations: {},
      requestRows: [],
      loading: false,
      matchingUsers: {},
      columns: [
        {
          name: "expandNotes",
          align: "left",
          label: "",
          headerStyle: "width: 2%",
        },
        {
          name: "requestType",
          align: "left",
          label: "Request Type",
          headerStyle: "width: 5%",
        },
        {
          name: "firstName",
          required: true,
          label: "First Name",
          align: "left",
          field: "firstName",
          sortable: true,
          headerStyle: "width: 5%",
        },
        {
          name: "lastName",
          required: true,
          label: "Last Name",
          align: "left",
          field: "lastName",
          sortable: true,
          headerStyle: "width: 5%",
        },
        {
          name: "phoneNumber",
          required: true,
          label: "Phone Number",
          align: "left",
          field: "phoneNumber",
          headerStyle: "width: 5%",
        },
        {
          name: "email",
          required: true,
          label: "Email",
          align: "left",
          field: "email",
          headerStyle: "width: 15%",
        },
        {
          name: "briefSetId",
          required: true,
          label: "Brief Set",
          align: "left",
          field: "briefSetId",
          headerStyle: "width: 10%",
        },
        {
          name: "roleId",
          required: true,
          label: "Role",
          align: "left",
          field: "roleId",
          headerStyle: "width: 10%",
        },
        {
          name: "organisationTeamId",
          required: true,
          label: "Team",
          align: "left",
          field: "organisationTeamId",
          headerStyle: "width: 10%",
        },
        {
          name: "welcomeUser",
          required: true,
          label: "Welcome User",
          align: "left",
          field: "organisationTeamId",
          headerStyle: "width: 10%",
        },
      ],
    };
  },
  computed: {
    requestsByType() {
      return groupBy(this.requestRows, "action");
    },
    activateUserRows() {
      return this.requestsByType.activate;
    },
    newUserRows() {
      return this.requestsByType.new_user;
    },
    deactivateUserRows() {
      return this.requestsByType.deactivate;
    },
    invalidForm() {
      return this.newUserRows?.some(
        (row) => !this.isPhoneNumberValid(row.phoneNumber)
      );
    },
  },
  mounted() {
    this.prepareRows();
  },
  methods: {
    kebabCase,
    toSentence,
    newUserRow(row) {
      return row.action === "new_user";
    },
    submit() {
      const actions = [];

      if (this.newUserRows) {
        let newUsersCount = this.newUserRows.length;
        let newUserWithWelcomeEmailCount = 0;
        let newUserExistingCredentialsCount = 0;

        this.newUserRows.forEach((row) => {
          if (row.sendWelcomeEmail) newUserWithWelcomeEmailCount += 1;
          if (row.matchingUsers.length) newUserExistingCredentialsCount += 1;
        });

        newUsersCount -= newUserExistingCredentialsCount;

        let message = "";

        if (newUsersCount > 0) {
          message += `
            <strong class="column text-ds-green-5">
              ${newUsersCount} new ${pluralize(
                newUsersCount,
                "user"
              )} will be created
            </strong>
          `;
        }

        if (newUserExistingCredentialsCount > 0) {
          message += `
            <strong class="column text-ds-green-5">
              ${newUserExistingCredentialsCount} ${pluralize(
                newUserExistingCredentialsCount,
                "existing user"
              )} will be added to another organisation
            </strong>
          `;
        }

        message += `
          <strong class="column text-ds-green-5">
            ${newUserWithWelcomeEmailCount} new ${pluralize(
              newUserWithWelcomeEmailCount,
              "user"
            )} will be sent welcome email
          </strong>
        `;

        actions.push(message);
      }

      if (this.deactivateUserRows) {
        const deactivateUserCount = this.deactivateUserRows.length;

        actions.push(`
          <strong class="text-ds-red-5">
            ${deactivateUserCount} ${pluralize(
              deactivateUserCount,
              "user"
            )} will be deactivated
          </strong>
        `);
      }

      if (this.activateUserRows) {
        const activateUserCount = this.activateUserRows.length;

        const activeUserWithWelcomeEmailCount = this.activateUserRows.filter(
          (row) => row.sendWelcomeEmail
        ).length;

        actions.push(`
          <strong class="text-ds-green-5">
            ${activateUserCount} ${pluralize(
              activateUserCount,
              "user"
            )} will be activated
          </strong>
        `);

        if (activeUserWithWelcomeEmailCount) {
          actions.push(`
          <strong class="column text-ds-green-5">
            ${activeUserWithWelcomeEmailCount} new ${pluralize(
              activeUserWithWelcomeEmailCount,
              "user"
            )} will be sent welcome email
          </strong>
        `);
        }
      }

      this.$dialog({
        theme: "danger",
        title: "Confirm Approval",
        message: `<p>By proceeding, ${actions.join(" and ")}</p>`,
        confirmLabel: `Approve ${this.requestRows.length} ${pluralize(
          this.requestRows.length,
          "Request"
        )} `,
      }).onOk(() => {
        this.$emit("approve", this.requestRows);
      });
    },
    async prepareRows() {
      try {
        this.loading = true;

        await Promise.all([
          this.loadOrganisationDefaults(),
          this.loadMatchingUsers(),
        ]);

        this.requestRows = this.buildRowsWithDefaults();
      } catch (error) {
        this.$error("Error preparing User Request rows", error);
      } finally {
        this.loading = false;
      }
    },
    buildRowsWithDefaults() {
      let requests = this.userRequests;

      if (this.userRequests.constructor !== Array) {
        requests = [this.userRequests];
      }

      return requests.map((request) => {
        const organisationId = request.requester.organisation.id;
        const organisationData = this.organisations[organisationId] || {};
        const user = {};

        if (request.user) {
          Object.entries(request.user).forEach(([key, value]) => {
            if (typeof value !== "undefined") user[key] = value;
          });

          const [firstName, ...lastName] = user.name.split(" ");
          user.firstName = firstName;
          user.lastName = lastName.join(" ");
        }

        let organisationTeam = {};

        if (request.team) {
          organisationTeam =
            organisationData.organisationTeams.find(
              (team) => team.name.toLowerCase() === request.team.toLowerCase()
            ) || {};
        }

        return {
          ...request,
          ...user,
          object: request.object,
          organisationTeamId:
            organisationTeam.id || organisationData.defaultTeamId,
          teamNotFound: request.team && !organisationTeam.id,
          organisationTeamIds: request.organisationTeamIds,
          roleId: organisationData.defaultRoleId,
          briefSetId: parseInt(organisationData.defaultBriefSetId, 10) || null,
          sendWelcomeEmail: false,
          matchingUsers: this.matchingUsers[request.id] || [],
        };
      });
    },
    async loadMatchingUsers() {
      const newUserRequestIds = [];

      this.userRequests.forEach((request) => {
        if (this.newUserRow(request)) newUserRequestIds.push(request.id);
      });

      if (newUserRequestIds.length === 0) return;

      const { data: userRequests } = await UserRequest.where({
        id: newUserRequestIds,
      })
        .select(["id"])
        .selectExtra({ user_requests: ["matching_users"] })
        .all();

      this.matchingUsers = userRequests.reduce((acc, userRequest) => {
        acc[userRequest.id] = userRequest.matchingUsers;

        return acc;
      }, {});
    },
    async loadOrganisationDefaults() {
      this.loading = true;

      const organisationIds = new Set();

      [this.userRequests].flat().forEach((userRequest) => {
        if (userRequest.action === "new_user") {
          organisationIds.add(userRequest.requester.organisation.id);
        }
      });

      const organisations = (
        await Organisation.includes(["organisationTeams"])
          .select({
            organisations: [
              "defaultTeamId",
              "defaultRoleId",
              "defaultBriefSetId",
            ],
            organisationTeams: ["name"],
          })
          .where({ id: Array.from(organisationIds) })
          .all()
      ).data;

      organisations.forEach((organisation) => {
        this.organisations[organisation.id] = organisation;
      });
    },
    isPhoneNumberValid(value) {
      return !value || value.match(/^\+?\d{10,13}$/);
    },
    actionForRow(row) {
      if (row.action === "new_user" && row.matchingUsers.length > 0)
        return "New User (Existing)";

      return ACTIONS[row.action];
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(table) {
  table-layout: fixed;

  .wrap-table-content {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: initial;
    word-wrap: break-word;
    overflow-wrap: break-word;
    font-size: 14px;
  }

  .nowrap-table-content {
    font-size: 14px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    max-width: 200px;
    padding: 2px 4px !important;
  }

  th.expand-notes {
    min-width: 50px;
    padding: 0 !important;
  }

  tr {
    &.new-user {
      background-color: rgba($positive, 0.025);
    }

    &.deactivate {
      background-color: rgba($danger, 0.025);
    }
  }
}

.break-email {
  @media screen and (width <= 951px) {
    word-break: break-all;
  }
}

tr:nth-child(even) {
  background-color: #f2f2f2;
}

.invalid-phone-number {
  font-size: 10px;
  color: $ds-red-4;
}
</style>
